import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Paywall removed: subscription is always 'active' (free for everyone)
  const [subscriptionStatus] = useState('active');

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('brandos_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch {
      localStorage.removeItem('brandos_token');
      localStorage.removeItem('brandos_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    if (res.data.requires_verification) {
      return { requires_verification: true, email: res.data.email };
    }
    localStorage.setItem('brandos_token', res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, password, name, ageConfirmed) => {
    const res = await authAPI.register({ email, password, name, age_confirmed: ageConfirmed });
    localStorage.setItem('brandos_token', res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('brandos_token');
    localStorage.removeItem('brandos_user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch { /* ignore */ }
  };

  const updateUserLocally = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  // Kept for backward compat in pages that still call this
  const checkSubscription = async () => ({ status: 'active', is_active: true });

  return (
    <AuthContext.Provider value={{ user, loading, subscriptionStatus, login, register, logout, refreshUser, checkSubscription, updateUserLocally }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
